// src/components/PostAd/DynamicAdForm.js
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { usePostAd } from '../../contexts/PostAdContext';
import { COMMON_FIELDS, CATEGORY_FORM_SPECS, FIELD_TYPES } from '../../constants/FormSpecs';
import { CATEGORIES_DATA } from '../../constants/Categories';
import { FiCamera, FiX } from 'react-icons/fi'; // Ensure icons are imported
import ModernSelect from './ModernSelect';
import { getCategoryBreadcrumbs } from '../../utils/categoryMapper';
import '../../pages/PostAd/PostAd.css';

const DynamicForm = () => {
  const { adData, flowType, selectedCategory, selectedSubCategory, setStep, setAdData, photos, setPhotos, previews, setPreviews, resetFlow } = usePostAd();

  const { register, handleSubmit, control, formState: { errors }, reset } = useForm({
    defaultValues: adData
  });

  useEffect(() => {
    reset(adData);
  }, [adData, reset]);

  const onSubmit = (data) => {
    setAdData(data);
    setStep(4); // Move to Location
  };

  const handleAddMorePhotos = (e) => {
    const newFiles = Array.from(e.target.files);
    const remaining = 10 - photos.length;
    const limitedFiles = newFiles.slice(0, remaining);

    setPhotos([...photos, ...limitedFiles]);
    const newPreviews = limitedFiles.map(file => URL.createObjectURL(file));
    setPreviews([...previews, ...newPreviews]);
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const getRelevantFields = () => {
    // 1. Find Sub-Category Name (e.g., "Computers & Laptops")
    const mainCat = CATEGORIES_DATA.find(c => c.id === selectedCategory);
    const subCatObj = mainCat?.subCategories?.find(s => s.id === selectedSubCategory);
    const subCatName = subCatObj?.name;

    // 2. Resolve Specific Fields: SubCat Name Override > MainCat ID > Empty
    const specificFields = 
      CATEGORY_FORM_SPECS[subCatName] || 
      CATEGORY_FORM_SPECS[selectedCategory] || 
      [];

    return [...COMMON_FIELDS, ...specificFields];
  };

  //const allFields = [...COMMON_FIELDS, ...(CATEGORY_FORM_SPECS[selectedCategory] || [])];

  const allFields = getRelevantFields();

  // Get readable names for breadcrumbs
  const breadcrumbs = getCategoryBreadcrumbs(selectedCategory, selectedSubCategory);

  return (
    <div className="dynamic-form-container">
      {/* 1. BREADCRUMBS */}
      <div className="category-breadcrumbs">
        <span>{breadcrumbs.categoryTitle}</span>
        {breadcrumbs.subCategoryTitle && (
          <>
            <span className="separator"> &gt; </span>
            <span>{breadcrumbs.subCategoryTitle}</span>
          </>
        )}
        <button className="change-cat-link" onClick={() => setStep(1.5)}>Change</button>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="dynamic-form">
        {flowType === 'AI' && <h2>Review Details</h2> || <h2>Tell us about your item</h2>}
        {/* 2. CONDITIONAL QOBO BADGE */}
        {flowType === 'AI' && (
          <p className="ai-badge-text">✨ Qobo has drafted this for you</p>
        )}

        {/* --- REFACTORED PHOTO MANAGER --- */}
        <div className="photo-manager">
          <label>Photos ({photos.length}/10)</label>
          <div className="photo-grid">
            {previews.map((url, index) => (
              <div key={url} className="photo-preview">
                <img src={url} alt="listing" />
                <button
                  type="button"
                  className="remove-photo-btn"
                  onClick={() => removePhoto(index)}
                >
                  <FiX />
                </button>
              </div>
            ))}

            {photos.length < 10 && (
              <label className="add-photo-placeholder">
                <input type="file" multiple accept="image/*" onChange={handleAddMorePhotos} hidden />
                <div className="add-more-content">
                <FiCamera size={24} />
                {photos.length < 10 && <span>Add Photos</span>}
                </div>
              </label>
            )}
          </div>
        </div>

        {/* --- FORM FIELDS --- */}
        {allFields.map((field) => (
          <div key={field.name} className="form-group">
            <label>{field.label} {field.required && '*'}</label>

            {field.type === FIELD_TYPES.TEXTAREA ? (
              <textarea {...register(field.name, { required: field.required })} placeholder={field.hint} />
            ) : field.type === FIELD_TYPES.DROPDOWN ? (
              // <select {...register(field.name, { required: field.required })}>
              //   <option value="">Select {field.label}</option>
              //   {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              // </select>

              <Controller
                name={field.name}
                control={control}
                rules={{ required: field.required }}
                render={({ field: { onChange, value } }) => (
                  <ModernSelect
                    options={field.options}
                    value={value}
                    onChange={onChange}
                    placeholder={`Select ${field.label}`}
                  />
                )}
              />
            ) : (
              <div className={field.prefix || field.suffix ? "input-with-affix" : ""}>
                {field.prefix && <span>{field.prefix}</span>}
                <input
                  type={field.type === FIELD_TYPES.NUMBER ? "number" : "text"}
                  {...register(field.name, { required: field.required })}
                  placeholder={field.hint}
                />
                {field.suffix && <span>{field.suffix}</span>}
              </div>
            )}
            {errors[field.name] && <span className="error">This field is required</span>}
          </div>
        ))}

        <button type="submit" className="continue-btn">Continue to Location</button>

        <center>
          <button type="button" className="reset-btn" onClick={resetFlow}>
            Cancel & Start Over
          </button>
        </center>
      </form>
    </div>
  );
};

export default DynamicForm;